import React, { useRef, useState } from 'react';

import { Box, Button, Grid, Group, Paper, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import GLMFlowBuilderMainContainer, {
  BuilderCard,
} from 'components/GLMFlowBuilder/GLMFlowBuilderMainContainer';
import KiloAlert from 'components/KiloAlert';
import PageHeader from 'components/PageHeader';
import { useAuth } from 'context/auth-context';
import useCreateKiloGLMFlows from 'hooks/glmFlows/useCreateKiloGLMFlows';
import useKiloGLMFLow from 'hooks/glmFlows/useKiloGLMFLow';
import useUpdateKiloGLMFlow from 'hooks/glmFlows/useUpdateKiloGLMFlow';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { useNavigate, useParams } from 'react-router-dom';
import { KiloGLMFlowWrite } from 'types';
import { getUpdatedFieldKeys, SelectorMap } from 'utils/compare-obj-functions';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

import GLMFlowBuilderSidebar from './components/GLMFlowBuilderSidebar';
import {
  ACTION_LABELS,
  FormValues,
  GLMCardTypes,
  KiloGLMFlowActions,
  KiloGLMFlowTriggers,
  TRIGGER_LABELS,
} from './types';

const PACKAGE_TRIGGERS = new Set<string>([
  KiloGLMFlowTriggers.PACKAGE_STARTED,
  KiloGLMFlowTriggers.PACKAGE_PURCHASED,
  KiloGLMFlowTriggers.PACKAGE_ENDED,
  KiloGLMFlowTriggers.PACKAGE_CANCELLED,
  KiloGLMFlowTriggers.PACKAGE_HOLD_START,
  KiloGLMFlowTriggers.PACKAGE_HOLD_END,
]);

const TAG_ACTIONS = new Set<string>([
  KiloGLMFlowActions.ADD_TAGS,
  KiloGLMFlowActions.REMOVE_TAGS,
  KiloGLMFlowActions.LINK_CONTACT_AND_ADD_TAG,
]);

const normalizePayload = (values: FormValues): KiloGLMFlowWrite => {
  const isTagAction = TAG_ACTIONS.has(values.action);
  const isPipelineAction = values.action === KiloGLMFlowActions.LINK_CONTACT_AND_MOVE_PIPELINE_STAGE;
  const base: KiloGLMFlowWrite = {
    kiloGlmFlowId: values.kiloGlmFlowId,
    trigger: values.trigger as KiloGLMFlowWrite['trigger'],
    action: values.action as KiloGLMFlowWrite['action'],
    enabled: values.enabled,
    acTags: isTagAction ? values.tags : null,
    acPipelineId: isPipelineAction ? values.acPipelineId : null,
    acStageId: isPipelineAction ? values.acStageId : null,
    tcAthleteStatus: null,
    tcCheckinsNumber: null,
    packageIds: [],
    classTypeIds: [],
    appointmentTypeIds: [],
  };

  if (PACKAGE_TRIGGERS.has(values.trigger)) {
    return { ...base, packageIds: values.packageIds ?? [] };
  }
  if (values.trigger === KiloGLMFlowTriggers.APPPOINTMENT_CHECKIN) {
    return { ...base, appointmentTypeIds: values.appointmentTypeIds ?? [] };
  }
  if (values.trigger === KiloGLMFlowTriggers.CLASS_CHECKIN) {
    return { ...base, classTypeIds: values.classTypeIds ?? [] };
  }
  if (values.trigger === KiloGLMFlowTriggers.ATHLETE_STATUS_CHANGED) {
    return { ...base, tcAthleteStatus: values.athleteStatus };
  }
  if (values.trigger === KiloGLMFlowTriggers.ATHLETE_CHECKINS_REACHED) {
    return { ...base, tcCheckinsNumber: values.checkinsNumber };
  }
  if (values.trigger === KiloGLMFlowTriggers.ATHLETE_DATA_UPDATED) {
    return { ...base, action: KiloGLMFlowActions.SYNC_CONTACT_DATA };
  }
  if (values.trigger === KiloGLMFlowTriggers.NOTE_CREATED_OR_EDITED) {
    return { ...base, action: KiloGLMFlowActions.UPSERT_NOTE };
  }
  if (values.trigger === KiloGLMFlowTriggers.NOTE_DELETED) {
    return { ...base, action: KiloGLMFlowActions.DELETE_NOTE };
  }
  return base;
};

// Field selectors for computing changed_fields on update (Mixpanel).
const CHANGED_FIELD_SELECTORS: SelectorMap = {
  enabled: (v: FormValues) => v.enabled,
  ac_tags: (v: FormValues) => v.tags ?? [],
  ac_pipeline_id: (v: FormValues) => v.acPipelineId,
  ac_stage_id: (v: FormValues) => v.acStageId,
  tc_athlete_status: (v: FormValues) => v.athleteStatus,
  tc_checkins_number: (v: FormValues) => v.checkinsNumber,
  tc_packages: (v: FormValues) => v.packageIds ?? [],
  tc_class_types: (v: FormValues) => v.classTypeIds ?? [],
  tc_appointment_types: (v: FormValues) => v.appointmentTypeIds ?? [],
};

// Resolve a selected id list to a comma-separated display string using the labels
// carried in form state (falls back to the id if a label is somehow missing).
const idsToLabelValue = (ids: string[] | null, labels: Record<string, string>): string | null => {
  if (!ids?.length) return null;
  return ids.map(id => labels?.[id] ?? id).join(', ');
};

// Build the ordered cards (trigger → config → action → action-config → activate)
// shown in the shared GLMFlowBuilderMainContainer as the flow is built.
const buildKiloGlmCards = (v: FormValues, selectedCard: GLMCardTypes): BuilderCard[] => {
  const cards: BuilderCard[] = [];
  if (!v.trigger) return cards;

  cards.push({
    key: 'trigger',
    cardType: 'trigger',
    headerLabel: 'Kilo Trigger',
    value: TRIGGER_LABELS[v.trigger],
    placeholderText: 'select trigger',
    isSelected: selectedCard === 'trigger',
  });

  // Trigger config card (varies by trigger).
  let hasConfig = true;
  if (PACKAGE_TRIGGERS.has(v.trigger)) {
    cards.push({
      key: 'package',
      cardType: 'package',
      headerLabel: 'Package',
      value: idsToLabelValue(v.packageIds, v.packageLabels),
      placeholderText: 'select package',
      isSelected: selectedCard === 'package',
    });
  } else if (v.trigger === KiloGLMFlowTriggers.ATHLETE_STATUS_CHANGED) {
    cards.push({
      key: 'status',
      cardType: 'status',
      headerLabel: 'Athlete Status',
      value: v.athleteStatus,
      placeholderText: 'select status',
      isSelected: selectedCard === 'status',
    });
  } else if (v.trigger === KiloGLMFlowTriggers.ATHLETE_CHECKINS_REACHED) {
    cards.push({
      key: 'num_checkins',
      cardType: 'num_checkins',
      headerLabel: 'Check-ins',
      value: (v.checkinsNumber ?? 0) > 0 ? `Check-in ${v.checkinsNumber}` : null,
      placeholderText: 'select number of check-ins',
      isSelected: selectedCard === 'num_checkins',
    });
  } else if (v.trigger === KiloGLMFlowTriggers.APPPOINTMENT_CHECKIN) {
    cards.push({
      key: 'appointment_type',
      cardType: 'appointment_type',
      headerLabel: 'Appointment Type',
      value: idsToLabelValue(v.appointmentTypeIds, v.appointmentTypeLabels),
      placeholderText: 'select appointment type',
      isSelected: selectedCard === 'appointment_type',
    });
  } else if (v.trigger === KiloGLMFlowTriggers.CLASS_CHECKIN) {
    cards.push({
      key: 'class_type',
      cardType: 'class_type',
      headerLabel: 'Class Type',
      value: idsToLabelValue(v.classTypeIds, v.classTypeLabels),
      placeholderText: 'select class type',
      isSelected: selectedCard === 'class_type',
    });
  } else {
    hasConfig = false;
  }

  const configFilled =
    !hasConfig ||
    !!v.packageIds?.length ||
    !!v.athleteStatus ||
    (v.checkinsNumber ?? 0) > 0 ||
    !!v.appointmentTypeIds?.length ||
    !!v.classTypeIds?.length;
  if (!configFilled) return cards;

  cards.push({
    key: 'action',
    cardType: 'action',
    headerLabel: 'GLM Action',
    value: v.action ? ACTION_LABELS[v.action] : null,
    placeholderText: 'select action',
    isSelected: selectedCard === 'action',
  });

  // Action config card.
  const isTagAction = TAG_ACTIONS.has(v.action);
  const isPipelineAction = v.action === KiloGLMFlowActions.LINK_CONTACT_AND_MOVE_PIPELINE_STAGE;
  if (isTagAction) {
    cards.push({
      key: 'tag',
      cardType: 'tag',
      headerLabel: 'Tags',
      value: v.tags?.length ? v.tags.join(', ') : null,
      placeholderText: 'select tags',
      isSelected: selectedCard === 'tag',
    });
  } else if (isPipelineAction) {
    cards.push({
      key: 'pipeline_stage',
      cardType: 'pipeline_stage',
      headerLabel: 'Pipeline & Stage',
      value: v.acPipelineId && v.acStageId ? 'Pipeline stage selected' : null,
      placeholderText: 'select pipeline & stage',
      isSelected: selectedCard === 'pipeline_stage',
    });
  }

  const actionConfigFilled =
    (isTagAction && !!v.tags?.length) ||
    (isPipelineAction && !!v.acPipelineId && !!v.acStageId) ||
    (!!v.action && !isTagAction && !isPipelineAction);
  if (actionConfigFilled) {
    cards.push({
      key: 'enabled',
      cardType: 'enabled',
      headerLabel: 'Activate',
      value: v.enabled ? 'Enabled' : null,
      placeholderText: 'Turn on',
      isSelected: selectedCard === 'enabled',
    });
  }

  return cards;
};

const formId = 'create-kilo-glm-flow';
const GLMFlowBuilder = (): JSX.Element => {
  const { session } = useAuth();
  const isDemoGym = !!session?.demoGym;
  const [selectedCard, setSelectedCard] = useState<GLMCardTypes>('trigger');
  const [cardHistory, setCardHistory] = useState<GLMCardTypes[]>([]);
  const navigate = useNavigate();
  const createMutation = useCreateKiloGLMFlows();
  const updateMutation = useUpdateKiloGLMFlow();
  const { trackGLMFlowCreated, trackGLMFlowUpdated } = useStaffTracking();
  const initialValuesRef = useRef<FormValues | null>(null);
  const form = useForm<FormValues>({
    initialValues: {
      kiloGlmFlowId: '',
      trigger: '',
      action: '',
      enabled: false,
      tags: [],
      acPipelineId: null,
      acStageId: null,
      athleteStatus: '',
      checkinsNumber: null,
      packageIds: [],
      classTypeIds: [],
      appointmentTypeIds: [],
      packageLabels: {},
      appointmentTypeLabels: {},
      classTypeLabels: {},
    },
    validate: {
      trigger: value => (value ? null : 'Trigger is required'),
      action: value => (value ? null : 'Action is required'),
    },
  });
  const formRef = useRef<HTMLFormElement>(null);
  const [type, setType] = useState<'create' | 'edit'>('create');
  const { flowId } = useParams<{ flowId: string }>();
  const { data: flowData, isSuccess } = useKiloGLMFLow(flowId || '');

  const handleTriggerSelect = (value: string): void => {
    form.setValues(prev => ({
      ...prev,
      trigger: value as FormValues['trigger'],
      action: '',
      tags: [],
      acPipelineId: null,
      acStageId: null,
      athleteStatus: '',
      checkinsNumber: null,
      packageIds: [],
      classTypeIds: [],
      appointmentTypeIds: [],
      packageLabels: {},
      appointmentTypeLabels: {},
      classTypeLabels: {},
    }));
  };

  const handleActionSelect = (value: string): void => {
    form.setValues(prev => ({
      ...prev,
      action: value as FormValues['action'],
      tags: [],
      acPipelineId: null,
      acStageId: null,
    }));
  };

  const handleTagSelect = (tags: string[]): void => {
    form.setFieldValue('tags', tags);
  };
  const handleFormReset = (): void => {
    form.reset();
    setSelectedCard(null);
    setCardHistory([]);
    navigate('/integrations/glm?tab=kilo-to-glm');
  };

  const handleCardSelect = (cardType: GLMCardTypes): void => {
    setCardHistory(prev => [...prev, selectedCard]);
    setSelectedCard(cardType);
  };

  const handleBack = (): void => {
    if (cardHistory.length === 0) return;
    setSelectedCard(cardHistory[cardHistory.length - 1]);
    setCardHistory(prev => prev.slice(0, -1));
  };

  const handleSubmit = (): void => {
    const { successNotification, errorNotification } = notificationHandler();
    if (type === 'create') {
      const payload = normalizePayload({ ...form.values, kiloGlmFlowId: uuidv4() });
      createMutation.mutate(payload, {
        onError: error => {
          errorNotification('Kilo GLM Flow Creation Failed', error.response?.data.message);
        },
        onSuccess: () => {
          trackGLMFlowCreated({
            flow_id: payload.kiloGlmFlowId,
            direction: 'kilo_to_glm',
            trigger: payload.trigger,
            action: payload.action,
            enabled: payload.enabled,
          });
          navigate('/integrations/glm?tab=kilo-to-glm');
          successNotification('Kilo GLM Flow Created', 'Flow has been created successfully');
        },
      });
    }
    if (type === 'edit') {
      const payload = normalizePayload(form.values);
      const changedFields = initialValuesRef.current
        ? getUpdatedFieldKeys(initialValuesRef.current, form.values, CHANGED_FIELD_SELECTORS)
        : [];
      updateMutation.mutate(payload, {
        onError: error => {
          errorNotification('Kilo GLM Flow Updation Failed', error.response?.data.message);
        },
        onSuccess: () => {
          trackGLMFlowUpdated({
            flow_id: payload.kiloGlmFlowId,
            direction: 'kilo_to_glm',
            trigger: payload.trigger,
            action: payload.action,
            changed_fields: changedFields,
          });
          navigate('/integrations/glm?tab=kilo-to-glm');
          successNotification('Kilo GLM Flow Updated', 'Flow has been updated successfully');
        },
      });
    }
  };

  useShallowEffect(() => {
    if (flowId && isSuccess && flowData) {
      setType('edit');
      const loadedValues: FormValues = {
        kiloGlmFlowId: flowData.data.kiloGlmFlowId,
        trigger: flowData.data.trigger as FormValues['trigger'],
        action: flowData.data.action as FormValues['action'],
        enabled: flowData.data.enabled,
        athleteStatus: flowData.data.tcAthleteStatus,
        acPipelineId: flowData.data.acPipelineId,
        acStageId: flowData.data.acStageId,
        tags: flowData.data.acTags ?? null,
        checkinsNumber: flowData.data.tcCheckinsNumber,
        packageIds: (flowData.data.tcPackages ?? []).map(p => p.packageId),
        classTypeIds: (flowData.data.tcClassTypes ?? []).map(c => c.kiloClassTypeId),
        appointmentTypeIds: (flowData.data.tcAppointmentTypes ?? []).map(a => a.appointmentTypeId),
        packageLabels: Object.fromEntries((flowData.data.tcPackages ?? []).map(p => [p.packageId, p.name])),
        appointmentTypeLabels: Object.fromEntries(
          (flowData.data.tcAppointmentTypes ?? []).map(a => [a.appointmentTypeId, a.name]),
        ),
        classTypeLabels: Object.fromEntries(
          (flowData.data.tcClassTypes ?? []).map(c => [c.kiloClassTypeId, c.name]),
        ),
      };
      form.setValues(loadedValues);
      // Snapshot for computing changed_fields on update.
      initialValuesRef.current = loadedValues;
    }
  }, [flowId, isSuccess, flowData]);

  return (
    <>
      <Paper
        shadow='xl'
        radius='md'
        withBorder
        py='sm'
        px={0}
        style={{ minWidth: 'fit-content', height: '100vh' }}
      >
        <form
          id={formId}
          ref={formRef}
          onSubmit={form.onSubmit(() => handleSubmit())}
          style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
          <PageHeader>
            <Group position='apart' align='center' noWrap>
              <Title order={1} size='h1'>
                Kilo To GLM Flow Builder
              </Title>
              <Group>
                <Button variant='subtle' color='red' onClick={handleFormReset}>
                  Cancel
                </Button>
                <Button
                  type='submit'
                  color='kiloBlue'
                  disabled={
                    !form.values.trigger ||
                    !form.values.action ||
                    (TAG_ACTIONS.has(form.values.action) && !form.values.tags?.length) ||
                    (form.values.action === KiloGLMFlowActions.LINK_CONTACT_AND_MOVE_PIPELINE_STAGE &&
                      (!form.values.acPipelineId || !form.values.acStageId))
                  }
                >
                  Save
                </Button>
              </Group>
            </Group>
          </PageHeader>
          {isDemoGym && (
            <Box px='md' pb='sm'>
              <KiloAlert variantStyle='warning'>
                This is a simulated Gym Lead Machine integration for demo purposes only. It is not connected
                to a real account, and any triggers you create here won&apos;t sync anywhere.
              </KiloAlert>
            </Box>
          )}
          <Grid style={{ flex: 1, overflow: 'hidden', margin: 0 }}>
            <Grid.Col span={8} style={{ height: '100%' }}>
              <GLMFlowBuilderMainContainer cards={buildKiloGlmCards(form.values, selectedCard)} />
            </Grid.Col>
            <Grid.Col span={4} style={{ height: '100%' }}>
              <GLMFlowBuilderSidebar
                type={type}
                form={form}
                selectedCard={selectedCard}
                handleTriggerSelect={handleTriggerSelect}
                handleActionSelect={handleActionSelect}
                handleTagSelect={handleTagSelect}
                onCardSelect={handleCardSelect}
                onBack={handleBack}
                onSave={() => formRef.current?.requestSubmit()}
                canGoBack={cardHistory.length > 0}
                stepNumber={cardHistory.length + 1}
              />
            </Grid.Col>
          </Grid>
        </form>
      </Paper>
    </>
  );
};
export default GLMFlowBuilder;
